import React from 'react';

import { css } from '@emotion/css';
import { TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { Button as AntdButton } from 'antd';

import { Tooltip } from 'components/Tooltip';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { hasUserPermission } from 'utils/roleAndTenant';

import * as Styled from './Button.style';

export type Props = Styled.ButtonProps & {
  /** Permissions the user must hold to click. Lacking any disables the button and explains why on hover. */
  permissionsRequired?: TenantUserPermission[];
};

export const Button = ({ permissionsRequired = [], ...props }: Props): JSX.Element => {
  const { activeTenant } = useGlobalApp();
  const isPermitted = permissionsRequired.every((permission) => hasUserPermission(activeTenant, permission));
  if (isPermitted) {
    return props?.type === 'text' ? <AntdButton {...props} /> : <Styled.Button {...props} />;
  }

  // a disabled button swallows mouse events, so the tooltip listens on a wrapper span instead (mirrors antd's Tooltip)
  const deniedProps = { ...props, disabled: true, style: { ...props.style, pointerEvents: 'none' as const } };
  return (
    <Tooltip title="You do not have permission to perform this action">
      <span
        className={css`
          display: inline-block;
          cursor: not-allowed;
          /* antd spaces footer buttons with an .ant-btn sibling rule that the wrapper would otherwise break */
          .ant-modal-footer .ant-btn + & {
            margin-left: 8px;
          }
        `}
      >
        {deniedProps.type === 'text' ? <AntdButton {...deniedProps} /> : <Styled.Button {...deniedProps} />}
      </span>
    </Tooltip>
  );
};
